import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { FileText, Building2, Truck, Users, TrendingUp, Clock, Euro, Activity, AlertTriangle, ShieldAlert, CheckCircle, Send } from 'lucide-react';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  useEffect(() => {
    api.get('/dashboard/stats').then(r => setStats(r.data)).catch(() => {});
    api.get('/dashboard/alerts').then(r => setAlerts(r.data)).catch(() => {});
  }, []);

  const cards = stats ? [
    { icon: Building2, label: 'Projetos Ativos', value: stats.active_projects, color: '#0D7C3E', bg: '#E8F5EC' },
    { icon: FileText, label: 'Orcamentos', value: stats.budget_count, color: '#1E3A5F', bg: '#E8EDF4' },
    { icon: Clock, label: 'Pendentes', value: stats.pending_budgets, color: '#C67A09', bg: '#FFF4E0' },
    { icon: Truck, label: 'Veiculos', value: stats.vehicle_count, color: '#1E6DB5', bg: '#E3F0FC' },
    { icon: Users, label: 'Funcionarios', value: stats.employee_count, color: '#7C3AED', bg: '#F0E6FF' },
    { icon: Euro, label: 'Total Orcamentos', value: `${(stats.total_budget_value || 0).toLocaleString('pt-PT')}`, color: '#1E3A5F', bg: '#E8EDF4' },
    { icon: TrendingUp, label: 'Total Gasto', value: `${(stats.total_spent || 0).toLocaleString('pt-PT')}`, color: '#C4302B', bg: '#FDECEB' },
    { icon: Activity, label: 'Total Projetos', value: stats.project_count, color: '#0D7C3E', bg: '#E8F5EC' },
  ] : [];

  const criticalAlerts = alerts?.alerts?.filter(a => a.severity === 'critico') || [];
  const warningAlerts = alerts?.alerts?.filter(a => a.severity === 'aviso') || [];

  const sendEmailAlerts = async () => {
    setEmailSending(true); setEmailResult(null);
    try { const { data } = await api.post('/notifications/send-alerts'); setEmailResult(data); } catch { setEmailResult({ success: false, message: 'Erro' }); }
    finally { setEmailSending(false); }
  };

  return (
    <div className="animate-in pt-10 lg:pt-0">
      <div className="mb-6">
        <h1 className="text-xl font-bold">Painel de Controlo</h1>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Visao geral do sistema</p>
      </div>

      {/* Alerts section */}
      {alerts && (alerts.total_critical > 0 || alerts.total_warning > 0) && (
        <div className="mb-6" data-testid="dashboard-alerts">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
              <ShieldAlert size={16} /> Alertas de Expiracao ({alerts.total_critical + alerts.total_warning})
            </h2>
            <div className="flex items-center gap-2">
              {emailResult && <span className={`text-[10px] font-medium ${emailResult.success ? 'text-green-600' : 'text-red-600'}`}>{emailResult.message}</span>}
              <button data-testid="email-alerts-btn" onClick={sendEmailAlerts} disabled={emailSending} className="btn btn-outline btn-xs" title="Enviar alertas por email">
                {emailSending ? <span className="animate-spin inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full" /> : <><Send size={12} /> Email</>}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {criticalAlerts.map((a, i) => (
              <div key={`c-${i}`} data-testid={`alert-critical-${i}`} className="flex items-center gap-3 p-3 rounded-md" style={{ background: '#FDECEB', border: '1px solid #F5C6CB' }}>
                <AlertTriangle size={16} style={{ color: 'var(--error)', flexShrink: 0 }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold" style={{ color: 'var(--error)' }}>{a.message}</p>
                  <p className="text-xs" style={{ color: '#9B2C2C' }}>
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold mr-1.5 ${a.type === 'veiculo' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                      {a.type === 'veiculo' ? 'Veiculo' : 'Funcionario'}
                    </span>
                    {a.entity}
                  </p>
                </div>
                <span className="text-xs font-mono font-semibold" style={{ color: 'var(--error)' }}>{a.date}</span>
              </div>
            ))}
            {warningAlerts.map((a, i) => (
              <div key={`w-${i}`} data-testid={`alert-warning-${i}`} className="flex items-center gap-3 p-3 rounded-md" style={{ background: '#FFF4E0', border: '1px solid #FFD180' }}>
                <AlertTriangle size={16} style={{ color: 'var(--warning)', flexShrink: 0 }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold" style={{ color: '#8B6914' }}>{a.message}</p>
                  <p className="text-xs" style={{ color: '#7C5E10' }}>
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold mr-1.5 ${a.type === 'veiculo' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                      {a.type === 'veiculo' ? 'Veiculo' : 'Funcionario'}
                    </span>
                    {a.entity}
                  </p>
                </div>
                <span className="text-xs font-mono font-semibold" style={{ color: '#8B6914' }}>{a.date}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No alerts message */}
      {alerts && alerts.total_critical === 0 && alerts.total_warning === 0 && (
        <div className="mb-6 p-3 rounded-md flex items-center gap-2" style={{ background: '#E8F5EC', border: '1px solid #A3D9B1' }} data-testid="no-alerts">
          <CheckCircle size={16} style={{ color: 'var(--success)' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--success)' }}>Sem alertas de expiracao</p>
        </div>
      )}

      {!stats ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="metric-card h-20 animate-pulse" style={{ background: '#f0f2f5' }} />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {cards.map(({ icon: Icon, label, value, color, bg }) => (
            <div key={label} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`} className="metric-card">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: bg }}>
                  <Icon size={14} color={color} />
                </div>
                <span className="metric-label">{label}</span>
              </div>
              <p className="metric-value">{value}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
